import React, { useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import Breadcrumb from '../components/Breadcrumb';
import { useAuth } from '../contexts/AuthContext';
import { founderMaintenanceService } from '../services/api';
import { ShieldCheck, FileText, Trash2, Loader2, CheckCircle2, AlertTriangle, RefreshCw } from 'lucide-react';

const Badge = ({ ok, yes = 'YES', no = 'NO' }) => (
  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${ok ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{ok ? yes : no}</span>
);

const FounderMaintenancePage = () => {
  const { user } = useAuth();
  // Legal registration
  const [legal, setLegal] = useState(null);
  const [legalBusy, setLegalBusy] = useState(false);
  const [legalConfirm, setLegalConfirm] = useState(false);
  const [legalResult, setLegalResult] = useState(null);
  const [legalErr, setLegalErr] = useState('');

  // Fixtures
  const [fx, setFx] = useState(null);
  const [fxBusy, setFxBusy] = useState(false);
  const [fxConfirm, setFxConfirm] = useState(false);
  const [fxResult, setFxResult] = useState(null);
  const [fxErr, setFxErr] = useState('');

  const runLegalStatus = async () => {
    setLegalBusy(true); setLegalErr(''); setLegalResult(null); setLegalConfirm(false);
    try { setLegal(await founderMaintenanceService.legalStatus()); }
    catch (e) { setLegalErr(e?.response?.data?.detail || 'Dry check failed (Founder / Super Admin only).'); }
    finally { setLegalBusy(false); }
  };
  const runLegalRegister = async () => {
    setLegalBusy(true); setLegalErr('');
    try { setLegalResult(await founderMaintenanceService.legalRegister()); setLegalConfirm(false); await runLegalStatusSilent(); }
    catch (e) { setLegalErr(e?.response?.data?.detail || 'Registration failed.'); }
    finally { setLegalBusy(false); }
  };
  const runLegalStatusSilent = async () => { try { setLegal(await founderMaintenanceService.legalStatus()); } catch { /* noop */ } };

  const runFxDry = async () => {
    setFxBusy(true); setFxErr(''); setFxResult(null); setFxConfirm(false);
    try { setFx(await founderMaintenanceService.fixturesDryRun()); }
    catch (e) { setFxErr(e?.response?.data?.detail || 'Dry run failed (Founder / Super Admin only).'); }
    finally { setFxBusy(false); }
  };
  const runFxCleanup = async () => {
    setFxBusy(true); setFxErr('');
    try { setFxResult(await founderMaintenanceService.fixturesCleanup((fx?.matched || []).map((m) => m.id))); setFxConfirm(false); await runFxDrySilent(); }
    catch (e) { setFxErr(e?.response?.data?.detail || 'Cleanup failed.'); }
    finally { setFxBusy(false); }
  };
  const runFxDrySilent = async () => { try { setFx(await founderMaintenanceService.fixturesDryRun()); } catch { /* noop */ } };

  const current = legal?.current_complete_count ?? '—';

  if (user && user.role !== 'super_admin') {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="founder-maintenance-denied">
          <ShieldCheck className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <h1 className="text-xl font-semibold text-gray-900">Founder (Super Admin) only</h1>
          <p className="text-sm text-gray-500 mt-2">This maintenance area is restricted to the Founder / Super Admin account.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 pb-24" data-testid="founder-maintenance-page">
        <Breadcrumb items={[{ label: 'Founder Maintenance' }]} />
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck className="w-5 h-5 text-blue-800" />
          <h1 className="text-2xl font-bold text-gray-900">Founder Maintenance</h1>
        </div>
        <p className="text-sm text-gray-500 mb-6">
          Founder-only production data maintenance. Actions run against <span className="font-medium">this environment's own database &amp; storage</span>. Nothing is approved automatically.
        </p>

        {/* SECTION A — LEGAL REGISTRATION */}
        <section className="mb-8 border border-gray-200 rounded-xl bg-white p-4 sm:p-5" data-testid="fm-legal-section">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700 flex items-center gap-2"><FileText className="w-4 h-4 text-indigo-700" /> Legal Document Registration</h2>
            <span className="text-xs text-gray-500" data-testid="fm-legal-count">Current documents: <b>{current}</b> / {legal?.expected_count ?? 12}</span>
          </div>
          <p className="text-xs text-gray-400 mb-3">Registers the 8 authoritative legal documents into this environment. Idempotent — safe to re-run; never approves anything.</p>

          <div className="flex flex-wrap gap-2">
            <button onClick={runLegalStatus} disabled={legalBusy} data-testid="fm-legal-drycheck-btn"
              className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
              {legalBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Run Dry Check
            </button>
          </div>

          {legalErr && <div className="mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700" data-testid="fm-legal-error">{legalErr}</div>}

          {legal && (
            <div className="mt-4" data-testid="fm-legal-status">
              <p className="text-[11px] text-gray-400 mb-2">{legal.environment_note}</p>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[520px] text-sm">
                  <thead><tr className="text-left text-xs text-gray-500 border-b">
                    <th className="py-2 pr-3">Document</th><th className="py-2 px-2">Ver</th><th className="py-2 px-2">Action</th>
                    <th className="py-2 px-2">PDF</th><th className="py-2 px-2">DOCX</th><th className="py-2 px-2">Approval</th></tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {(legal.documents || []).map((d) => (
                      <tr key={d.doc_key} data-testid={`fm-legal-row-${d.doc_key}`}>
                        <td className="py-2 pr-3 font-medium text-gray-800">{d.doc_key}{d.packet_doc_key && <span className="ml-1 text-[10px] text-indigo-600">· {d.packet_doc_key}</span>}</td>
                        <td className="py-2 px-2 text-gray-600">{d.version}</td>
                        <td className="py-2 px-2"><span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${d.action === 'create' ? 'bg-amber-100 text-amber-700' : 'bg-blue-50 text-blue-700'}`}>{d.action === 'create' ? 'CREATE' : 'UPDATE'}</span></td>
                        <td className="py-2 px-2"><Badge ok={d.pdf_present} /></td>
                        <td className="py-2 px-2"><Badge ok={d.docx_present} /></td>
                        <td className="py-2 px-2 text-[11px] text-gray-500">{d.approval_status === 'founder_approved' ? 'APPROVED' : 'AWAITING'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {!legalConfirm ? (
                <button onClick={() => setLegalConfirm(true)} disabled={legalBusy} data-testid="fm-legal-register-btn"
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-900 text-white rounded-lg hover:bg-blue-800 disabled:opacity-50">
                  Register Authoritative Documents
                </button>
              ) : (
                <div className="mt-4 border border-amber-300 bg-amber-50 rounded-lg p-3" data-testid="fm-legal-confirm">
                  <p className="text-sm font-medium text-amber-900 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Register {legal?.expected_count ?? 12} authoritative legal documents in THIS environment?</p>
                  <p className="text-xs text-amber-800 mt-1">Documents remain <b>AWAITING FOUNDER APPROVAL</b>. This does not send or approve anything.</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button onClick={() => setLegalConfirm(false)} disabled={legalBusy} data-testid="fm-legal-cancel-btn" className="px-3 py-2 text-sm rounded-lg text-gray-600 hover:bg-white border border-gray-200">Cancel</button>
                    <button onClick={runLegalRegister} disabled={legalBusy} data-testid="fm-legal-confirm-btn" className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-blue-900 text-white rounded-lg hover:bg-blue-800 disabled:opacity-50">
                      {legalBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Confirm — Register {legal?.expected_count ?? 12} Documents
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {legalResult && (
            <div className="mt-4 border border-green-200 bg-green-50 rounded-lg p-3 text-sm" data-testid="fm-legal-result">
              <p className="font-medium text-green-800 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Registered {legalResult.complete_count} / {legalResult.expected_count} · duplicates: {legalResult.duplicate_current_records ? 'YES' : 'NO'} · approved: {legalResult.documents_approved}</p>
            </div>
          )}
        </section>

        {/* SECTION B — FIXTURE CLEANUP */}
        <section className="border border-gray-200 rounded-xl bg-white p-4 sm:p-5" data-testid="fm-fixtures-section">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700 flex items-center gap-2 mb-1"><Trash2 className="w-4 h-4 text-rose-600" /> Synthetic Fixture Cleanup</h2>
          <p className="text-xs text-gray-400 mb-3">Removes known synthetic regression records ("Auditor Regression") from this environment. Aborts entirely if a protected record (Mark Goff) is ever matched.</p>

          <button onClick={runFxDry} disabled={fxBusy} data-testid="fm-fx-dryrun-btn"
            className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
            {fxBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Dry Run Cleanup
          </button>

          {fxErr && <div className="mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700" data-testid="fm-fx-error">{fxErr}</div>}

          {fx && (
            <div className="mt-4" data-testid="fm-fx-status">
              <p className="text-[11px] text-gray-400">{fx.environment_note}</p>
              <p className="text-sm text-gray-700 mt-1">Matched <b>{fx.match_count}</b> synthetic record(s) · would keep <b>{fx.would_keep}</b> · Mark Goff in candidates: <b className={fx.mark_goff_in_candidates ? 'text-red-600' : 'text-green-700'}>{fx.mark_goff_in_candidates ? 'YES' : 'NO'}</b></p>
              <p className="text-[11px] text-amber-700 mt-1">{fx.dependency_note}</p>

              {(fx.matched || []).length > 0 && (
                <div className="overflow-x-auto mt-3">
                  <table className="w-full min-w-[560px] text-sm">
                    <thead><tr className="text-left text-xs text-gray-500 border-b">
                      <th className="py-2 pr-3">ID</th><th className="py-2 px-2">Name</th><th className="py-2 px-2">Type</th><th className="py-2 px-2">Created</th><th className="py-2 px-2">Why matched</th></tr></thead>
                    <tbody className="divide-y divide-gray-100">
                      {fx.matched.map((m) => (
                        <tr key={m.id} data-testid={`fm-fx-row-${m.id}`}>
                          <td className="py-2 pr-3 font-mono text-[11px] text-gray-500">{(m.id || '').slice(0, 10)}…</td>
                          <td className="py-2 px-2 text-gray-800">{m.full_name}</td>
                          <td className="py-2 px-2 text-gray-600">{m.application_type}</td>
                          <td className="py-2 px-2 text-[11px] text-gray-500">{(m.created_at || '—').slice(0, 10)}</td>
                          <td className="py-2 px-2 text-[11px] text-gray-500">{m.match_reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {fx.match_count > 0 && (!fxConfirm ? (
                <button onClick={() => setFxConfirm(true)} disabled={fxBusy} data-testid="fm-fx-cleanup-btn"
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-rose-700 text-white rounded-lg hover:bg-rose-800 disabled:opacity-50">
                  Review &amp; Confirm Cleanup
                </button>
              ) : (
                <div className="mt-4 border border-rose-300 bg-rose-50 rounded-lg p-3" data-testid="fm-fx-confirm">
                  <p className="text-sm font-medium text-rose-900 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Permanently delete {fx.match_count} synthetic record(s) from THIS environment?</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button onClick={() => setFxConfirm(false)} disabled={fxBusy} data-testid="fm-fx-cancel-btn" className="px-3 py-2 text-sm rounded-lg text-gray-600 hover:bg-white border border-gray-200">Cancel</button>
                    <button onClick={runFxCleanup} disabled={fxBusy} data-testid="fm-fx-confirm-btn" className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-rose-700 text-white rounded-lg hover:bg-rose-800 disabled:opacity-50">
                      {fxBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Confirm Cleanup
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {fxResult && (
            <div className="mt-4 border border-green-200 bg-green-50 rounded-lg p-3 text-sm" data-testid="fm-fx-result">
              <p className="font-medium text-green-800 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> {fxResult.message}</p>
            </div>
          )}
        </section>

        <PreviewFounderSection />
      </div>
    </DashboardLayout>
  );
};

const PreviewFounderSection = () => {
  const [accounts, setAccounts] = React.useState([]);
  const [email, setEmail] = React.useState('');
  const [pw, setPw] = React.useState('');
  const [pw2, setPw2] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState(null);
  const [err, setErr] = React.useState('');

  React.useEffect(() => {
    founderMaintenanceService.previewFounderAccounts()
      .then((d) => { setAccounts(d.accounts || []); if ((d.accounts || [])[0]) setEmail(d.accounts[0].email); })
      .catch(() => setAccounts([]));
  }, []);

  const submit = async () => {
    setErr(''); setMsg(null);
    if (pw.length < 10) { setErr('Password must be at least 10 characters.'); return; }
    if (pw !== pw2) { setErr('Passwords do not match.'); return; }
    setBusy(true);
    try { const r = await founderMaintenanceService.resetPreviewFounder(email, pw); setMsg(r.message); setPw(''); setPw2(''); }
    catch (e) { setErr(e?.response?.data?.detail || 'Reset failed.'); } finally { setBusy(false); }
  };

  return (
    <section className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6" data-testid="fm-preview-founder">
      <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-blue-600" /> Preview Founder Access</h2>
      <p className="text-sm text-gray-600 mt-1">Reset the password of a designated Preview/test Founder account from the portal — no code, database, or environment changes. Production Founder credentials cannot be changed here.</p>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-gray-600 mb-1">Preview account</label>
          <select value={email} onChange={(e) => setEmail(e.target.value)} data-testid="fm-preview-account" className="w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500">
            {accounts.map((a) => <option key={a.email} value={a.email}>{a.email}{a.exists ? '' : ' (not in this environment)'}</option>)}
          </select>
        </div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">New password</label><input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="fm-preview-password" className="w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" /></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Confirm password</label><input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="fm-preview-password-confirm" className="w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500" /></div>
      </div>
      {err && <p role="alert" className="text-sm text-rose-600 mt-2" data-testid="fm-preview-error">{err}</p>}
      {msg && <p className="text-sm text-green-700 mt-2 flex items-center gap-1.5" data-testid="fm-preview-success"><CheckCircle2 className="w-4 h-4" /> {msg}</p>}
      <button onClick={submit} disabled={busy || !email} data-testid="fm-preview-reset-btn" className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Update password</button>
      <p className="text-[11px] text-gray-400 mt-2">The password is never displayed after saving. The change is audited without recording the password.</p>
    </section>
  );
};

export default FounderMaintenancePage;
